import ShareArrow from '@/assets/images/icons/share-arrow.svg?react';
import ZoomFit from '@/assets/images/icons/zoom-fit.svg?react';
import DateDisplay from '@/components/common/date-display';
import type { ImageZoomActions } from '@/components/common/media-content/image-zoom-pan';
import MediaContent from '@/components/common/media-content/media-content';
import { NativeShareButton } from '@/components/common/native-share-button';
import PageLayout from '@/components/layouts/page-layout/page-layout';
import DelayedLayout from '@/components/layouts/progress-layout/delayed-layout';
import { MediaStateProvider, useMediaState } from '@/components/media-context';
import useDeleteMessages from '@/hooks/conversations/use-delete-messages';
import useMediaMessages from '@/hooks/conversations/use-media-messages';
import useDownloadMessageFile from '@/hooks/use-download-message-file';
import useTapOrSwipeDown from '@/hooks/use-tap-or-swipe-down';
import { useIsDesktop } from '@/hooks/use-user-platform';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import type { ConversationType } from '@/models/conversations/conversation-session';
import ROUTES from '@/routes';
import { BUILD_EMBED_TYPE } from '@/utils/constants';
import { getSafeMediaUrl } from '@/utils/media-url';
import cn from '@/utils/merge-class-names';

import 'react-splide-ts/css';
import { Add, Category, ImportCurve, SearchZoomIn, SearchZoomOut1, Trash } from 'iconsax-react';
import { type RefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useTypedParams, useTypedSearchParams } from 'react-router-typesafe-routes';
import { Splide, SplideSlide } from 'react-splide-ts';

interface Props {
  type: ConversationType;
}

export default function MediaMessageByIdRoute({ type }: Props) {
  return (
    <MediaStateProvider>
      <MediaMessageById type={type} />
    </MediaStateProvider>
  );
}

export function MediaMessageById({ type }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id, messageId: mediaId } = useTypedParams(
    ROUTES.MESSAGES.GROUPS.MEDIA.BY_ID || ROUTES.MESSAGES.SESSIONS.MEDIA.BY_ID,
  );
  const { updatePlaybackTime, clearAllMediaState, playbackTimes } = useMediaState();
  const [{ filterTypes }] = useTypedSearchParams(
    ROUTES.MESSAGES.GROUPS.MEDIA.BY_ID || ROUTES.MESSAGES.SESSIONS.MEDIA.BY_ID,
  );

  const isDesktop = useIsDesktop();
  const splideRef = useRef<Splide>(null);
  const imageZoomRef = useRef<ImageZoomActions>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const [messageId, setMessageId] = useState(() => mediaId);
  const swipeAndTapHandlers = useTapOrSwipeDown({
    onSwipeDown: () => {
      handleDismiss();
    },
    onTap: () => {
      if (message?.MsgType === ConversationMessageContentType.Image) {
        handleDismiss();
      }
    },
  });

  const { messages, messageIndex, message } = useMediaMessages(messageId, 'forward', filterTypes);

  const { promptDeleteMessages } = useDeleteMessages(type, id);
  const { isDownloading, handleDownload } = useDownloadMessageFile(message);

  const shouldDarkMode = message
    ? [ConversationMessageContentType.Image, ConversationMessageContentType.Video].includes(message.MsgType)
    : false;

  const handleDelete = async () => {
    if (!message) return;

    if (await promptDeleteMessages([message])) navigate(-1);
  };

  const handleForward = () => {
    const href = (type === 'group' ? ROUTES.MESSAGES.GROUPS.FORWARD : ROUTES.MESSAGES.SESSIONS.FORWARD).$buildPath({
      params: {
        id,
      },
      searchParams: {
        messageIds: [messageId],
      },
    });

    navigate(href, { replace: true });
  };

  const handleDismiss = () => {
    if (!isDesktop) {
      navigate(-1);
    }
  };

  const handleActiveSlideChange = (slideIndex: number) => {
    const activeSlideMessage = messages.at(slideIndex);
    if (!activeSlideMessage) return;
    setMessageId(activeSlideMessage.ID);
    if (activeSlideMessage.ID === messageId) return;

    const href = (
      type === 'group' ? ROUTES.MESSAGES.GROUPS.MEDIA.BY_ID : ROUTES.MESSAGES.SESSIONS.MEDIA.BY_ID
    ).$buildPath({
      params: {
        id,
        messageId: activeSlideMessage.ID,
      },
    });

    if (BUILD_EMBED_TYPE !== 'embed-native') {
      history.replaceState({ ...history.state }, '', href);
    } else {
      navigate(href, { replace: true });
    }

    if (BUILD_EMBED_TYPE !== 'embed-native') {
      history.replaceState({ ...history.state }, '', href);
    } else {
      navigate(href, { replace: true });
    }
  };

  const enableSwipe = (enable: boolean) => {
    const splide = splideRef.current?.splide;
    if (splide) {
      splide.options = {
        ...splide.options,
        drag: enable,
      };
    }
  };

  useEffect(() => {
    handleActiveSlideChange(messageIndex);
  }, [messageIndex]);

  return (
    <PageLayout
      headerConfig={{
        className: cn(
          'transition-colors mode-embedded:bg-primary ',
          {
            'text-neutral-0 bg-neutral-950': shouldDarkMode,
          },
          'layout-mini:text-base-content',
        ),
        actions: (
          <button
            type='button'
            className='btn btn-ghost p-0'
            onClick={() => {
              navigate(-1);
            }}>
            <Add className='rotate-45 size-6' />
          </button>
        ),
        headerLabel: message && (
          <div className='grid grid-cols-[1fr_auto_1fr] w-full items-center gap-2'>
            <div className='flex justify-start'>
              <NativeShareButton
                content={message && { type: 'message', data: message }}
                className={cn(' text-neutral-0', {
                  'text-base-content': !shouldDarkMode,
                })}
              />
            </div>

            <div className='flex flex-col text-sm size-full flex-center'>
              <span className='font-bold'>{message.UserNickName}</span>
              <DateDisplay
                ruleName='collection-preview'
                timestamp={message.CreateTime}
              />
            </div>

            <MediaActions
              key='media-actions'
              sessionType={type}
              imageZoomRef={imageZoomRef}
              mediaType={message?.MsgType}
            />
          </div>
        ),
      }}
      title={t('my-collection.media')}
      bannerColor={shouldDarkMode ? 'dark' : 'light'}
      className={cn('flex flex-col gap-1 transition-colors', {
        'text-neutral-0 bg-neutral-950': shouldDarkMode,
      })}>
      <DelayedLayout
        delay={300}
        className={cn({ 'flex-1 w-screen': !isDesktop })}>
        {() => (
          <Splide
            onDestroy={() => {
              clearAllMediaState();
            }}
            onDragging={() => {
              if (videoRef.current) {
                videoRef.current?.pause();
              }
            }}
            onDragged={() => {
              if (videoRef.current) {
                updatePlaybackTime(videoRef.current.src, videoRef.current.currentTime);
              }
            }}
            ref={splideRef}
            options={{
              perPage: 1,
              perMove: 1,
              flickMaxPages: 1,
              flickPower: 100,
              flickVelocity: 0.5,
              drag: true,
              arrows: false,
              pagination: false,
              start: messageIndex,
              gap: '0.25rem',
              fixedWidth: '100vw',
            }}
            className={cn('flex-1', { 'carousel w-screen flex': !isDesktop })}
            onActive={(slide) => handleActiveSlideChange(slide.index)}
            {...swipeAndTapHandlers}>
            {messages.map((m) => (
              <SplideSlide
                key={`media-message-${m.ID}`}
                className='desktop:h-[calc(100vh-8rem)]'>
                <MediaContent
                  ref={
                    m.ID === messageId && m.MsgType === ConversationMessageContentType.Image
                      ? imageZoomRef
                      : m.MsgType === ConversationMessageContentType.Video
                        ? videoRef
                        : null
                  }
                  autoPlay
                  isInView={m.ID === messageId}
                  type={m.MsgType}
                  src={m.Message}
                  playBackTime={
                    m.MsgType === ConversationMessageContentType.Video
                      ? playbackTimes?.[getSafeMediaUrl(m.Message)]
                      : undefined
                  }
                  onImageZoom={enableSwipe}
                />
              </SplideSlide>
            ))}
          </Splide>
        )}
      </DelayedLayout>

      <div
        className={cn(
          'flex items-center justify-around desktop:py-3 desktop:justify-end desktop:gap-2 desktop:mr-4 pb-safe-offset-2 w-full bg-neutral-950',
          {
            'bg-neutral-0': !shouldDarkMode,
          },
        )}>
        <button
          type='button'
          className='btn btn-ghost flex-nowrap min-h-none desktop:gap-2 desktop:h-10 disabled:invert'
          onClick={handleDelete}>
          <Trash
            variant='Outline'
            size={24}
          />
          <span className='hidden desktop:inline-block'>{t('conversation.delete.delete')}</span>
        </button>

        <button
          type='button'
          disabled={isDownloading}
          className='btn btn-ghost flex-nowrap min-h-none desktop:gap-2 desktop:h-10 disabled:invert'
          onClick={handleDownload}>
          {isDownloading ? (
            <span className='loading loading-spinner' />
          ) : (
            <ImportCurve
              variant='Outline'
              size={24}
            />
          )}
          <span className='hidden desktop:inline-block'>{t('common.save')}</span>
        </button>

        {!message?.ReadDelete && (
          <button
            type='button'
            className='btn btn-ghost flex-nowrap min-h-none desktop:gap-2 desktop:h-10 disabled:invert'
            onClick={handleForward}>
            <ShareArrow className='size-6' />
            <span className='hidden desktop:inline-block'>{t('conversation.forward.forward')}</span>
          </button>
        )}
      </div>
    </PageLayout>
  );
}

function MediaActions({
  mediaType,
  imageZoomRef,
  sessionType,
}: {
  mediaType: ConversationMessageContentType | undefined;
  imageZoomRef: RefObject<ImageZoomActions>;
  sessionType: ConversationType;
}) {
  const { id } = useTypedParams(ROUTES.MESSAGES.GROUPS.MEDIA.BY_ID || ROUTES.MESSAGES.SESSIONS.MEDIA.BY_ID);

  const zoomButtons = [
    { key: 'zoom-in', icon: <SearchZoomIn />, action: () => imageZoomRef?.current?.zoomIn() },
    { key: 'zoom-out', icon: <SearchZoomOut1 />, action: () => imageZoomRef?.current?.zoomOut() },
    { key: 'zoom-fit', icon: <ZoomFit className='size-6' />, action: () => imageZoomRef?.current?.resetTransform() },
  ];

  const navigateToMediaList = ROUTES.MESSAGES[sessionType === 'group' ? 'GROUPS' : 'SESSIONS'].MEDIA.$buildPath({
    params: { id },
  });

  return (
    <div className='flex justify-end items-center'>
      <div className='items-center gap-4 min-w-fit hidden desktop:flex'>
        {mediaType === ConversationMessageContentType.Image && (
          <>
            {zoomButtons.map(({ key, icon, action }) => (
              <button
                key={key}
                type='button'
                className='btn btn-ghost p-0'
                onClick={action}>
                {icon}
              </button>
            ))}
            <div className='h-6 w-px me-4 bg-neutral-0' />
          </>
        )}
      </div>

      <Link
        replace
        className='btn btn-ghost p-0 '
        to={navigateToMediaList}>
        <Category variant='Bulk' />
      </Link>

      <div className='h-6 w-px ms-4 bg-neutral-0' />
    </div>
  );
}
